import { Box, Icon, Image, Typography } from "@outschool/backpack";
import {
  EnrollmentMeetingPurchaseDuration_ActivityFragment,
  EnrollmentMeetingPurchaseRecurringQueryQuery,
  EnrollmentMeetingPurchaseRecurringQueryQueryVariables
} from "@outschool/gql-frontend-generated";
import { faCheck, falTimes } from "@outschool/icons";
import { TFunction, useTranslation } from "@outschool/localization";
import {
  dayjs,
  fullDurationStringWithStartTime,
  weeklyDayOfWeekWithDuration
} from "@outschool/time";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import {
  IconButton,
  LoadingModal,
  ModalActionButtons
} from "@outschool/ui-legacy-component-library";
import React from "react";

import onetimeImage from "../../../images/calendar-checkmark.png";
import recurringImage from "../../../images/calendar-recurring.png";
import { useTimeZone } from "../../providers/TimeZoneProvider";
import { EnrollmentMeetingPurchaseRecurringQuery } from "../../queries/EnrollmentMeetingPurchaseRecurringQuery";
import { EnrollmentDurationSelect } from "./enrollmentForm/EnrollmentDurationSelect";
import PayAsYouGoTag from "./enrollmentForm/PayAsYouGoTag";
import RefundPolicy from "./enrollmentForm/RefundPolicy";
import { MAX_DAYS_AVAILABILITY } from "./EnrollmentMeetingPurchaseWithStripe";
import { FutureMeetingInfo } from "./types";

export type EnrollmentMeetingPurchaseDurationProps = {
  startTime: Date;
  endTime: Date;
  activity: EnrollmentMeetingPurchaseDuration_ActivityFragment;
  canPayLater: boolean;
  onBack(e: React.SyntheticEvent): void;
  onClose(e: React.SyntheticEvent, resetFields?: boolean): void;
  onNextClick({
    duration,
    canScheduleContinuously,
    futureMeetings
  }: {
    duration: number | null;
    canScheduleContinuously: boolean;
    futureMeetings: FutureMeetingInfo[];
  }): void;
  showCloseButton?: boolean;
  orgTeacherUid?: string | null;
  showCancel?: boolean;
  resetFields?: boolean;
};

export function useFutureMeetings({
  activityUid,
  startTime,
  endTime,
  enrollmentDuration,
  orgTeacherUid
}: {
  activityUid: string;
  startTime: Date;
  endTime: Date;
  enrollmentDuration?: number | null;
  orgTeacherUid?: string | null;
}) {
  const { data, loading: futureMeetingsLoading } = useQueryWithPreviousData<
    EnrollmentMeetingPurchaseRecurringQueryQuery,
    EnrollmentMeetingPurchaseRecurringQueryQueryVariables
  >(EnrollmentMeetingPurchaseRecurringQuery, {
    fetchPolicy: "cache-and-network",
    skip: !activityUid || !startTime || !endTime,
    variables: {
      activityUid,
      startTime,
      endTime,
      orgTeacherUid,
      includeFirstMeeting: true
    }
  });

  return React.useMemo(() => {
    const futureEnrollmentMeetingAvailability =
      data?.activity?.futureEnrollmentMeetingAvailability ?? [];
    const canScheduleContinuously =
      data?.activity?.canScheduleContinuously ?? false;

    // The server only calculates availability for up to 90 days, anything beyond
    // is considered unavailable. When displaying future dates and their availability
    // for now, default to showing them as available.
    const futureMeetingAvailabilityBeyond90Days =
      futureEnrollmentMeetingAvailability.map(meetingInfo => {
        const { isAvailable, startTime } = meetingInfo;
        const beyond90Days =
          dayjs(startTime).diff(dayjs(), "days") > MAX_DAYS_AVAILABILITY;
        return {
          ...meetingInfo,
          isAvailable: isAvailable || beyond90Days
        };
      });

    // The default enrollment duration is null (continuous). If there's a conflicting
    // future continuous enrollment, we can't keep null as the default, so switch to
    // the first duration option with teacher availability.
    const weeksToFirstAvailableDate =
      futureMeetingAvailabilityBeyond90Days.findIndex(
        meeting => meeting.isAvailable
      ) + 1;
    const available = futureMeetingAvailabilityBeyond90Days?.filter(
      meeting => meeting.isAvailable
    );
    const durations: number[] = [1];
    if (available) {
      const totalWeeksAvailable = available.length;
      if (totalWeeksAvailable >= 4) {
        durations.unshift(4);
      }
      if (totalWeeksAvailable >= 8) {
        durations.unshift(8);
      }
      if (totalWeeksAvailable >= 12) {
        durations.unshift(12);
      }
    }

    const durationsWithAvailability = durations.filter(
      duration => weeksToFirstAvailableDate <= duration
    );

    const finalDuration =
      enrollmentDuration === null && !canScheduleContinuously
        ? durationsWithAvailability[0]
        : enrollmentDuration;

    return {
      futureMeetingsLoading,
      duration: finalDuration,
      canScheduleContinuously,
      futureMeetings: futureMeetingAvailabilityBeyond90Days,
      durationsWithAvailability
    };
  }, [data, futureMeetingsLoading, enrollmentDuration]);
}

export default function EnrollmentMeetingPurchaseDuration({
  startTime,
  endTime,
  activity,
  onBack,
  onClose,
  onNextClick,
  showCloseButton = true,
  orgTeacherUid,
  canPayLater = false,
  showCancel = false,
  resetFields = false
}: EnrollmentMeetingPurchaseDurationProps) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\EnrollmentMeetingPurchaseDuration"
  );
  const [enrollmentDuration, setEnrollmentDuration] = React.useState(null);
  const [multiEnrollment, setMultiEnrollment] = React.useState(true);

  const {
    duration,
    canScheduleContinuously,
    futureMeetings,
    futureMeetingsLoading
  } = useFutureMeetings({
    activityUid: activity.uid,
    startTime,
    endTime,
    enrollmentDuration,
    orgTeacherUid
  });

  if (futureMeetingsLoading) {
    return <LoadingModal />;
  }

  const onNext = (e: MouseEvent) => {
    e.preventDefault();
    onNextClick({
      // @ts-ignore TS(2322): Type 'number | null' is not assignable to type 'nu... Remove this comment to see the full error message
      duration: multiEnrollment ? duration : 1,
      canScheduleContinuously,
      futureMeetings
    });
  };

  const onCancel = (e: React.MouseEvent) => {
    if (showCancel) {
      onClose(e);
    } else if (resetFields) {
      onClose(e, resetFields);
    } else {
      onBack(e);
    }
  };
  return (
    <form method="POST" onSubmit={e => e.preventDefault()}>
      <Box
        flex
        sx={{
          gap: "1em",
          flexDirection: "column",
          alignItems: "stretch",
          justifyContent: "space-between",
          padding: "24px 0"
        }}
      >
        <RecurringCard
          startTime={startTime}
          endTime={endTime}
          // @ts-ignore TS(2322): Type 'null' is not assignable to type 'number'.
          enrollmentDuration={enrollmentDuration}
          // @ts-ignore TS(2322): Type 'Dispatch<SetStateAction<null>>' is not assig... Remove this comment to see the full error message
          setEnrollmentDuration={setEnrollmentDuration}
          selected={multiEnrollment}
          isRecurringSchedulingAvailable={canScheduleContinuously}
          onClick={() => setMultiEnrollment(true)}
          t={t}
          canPayLater={canPayLater}
        />
        <OnetimeCard
          startTime={startTime}
          endTime={endTime}
          selected={!multiEnrollment}
          onClick={() => setMultiEnrollment(false)}
          t={t}
        />
        <RefundPolicy priceCents={activity.price_cents} />
        {showCloseButton && (
          <Box
            sx={{
              position: "absolute",
              top: "8px",
              right: "8px",
              zIndex: 1
            }}
          >
            <IconButton
              icon={falTimes}
              sx={{
                fontSize: "large",
                color: "text",
                "&:hover": {
                  color: "#380596"
                }
              }}
              iconProps={{ sx: { fontSize: "1.333em" } }}
              onClick={onClose}
              aria-label={t("Close")}
            />
          </Box>
        )}
      </Box>
      <ModalActionButtons
        cancelLabel={!showCancel ? t("Back") : t("Cancel")}
        onCancel={onCancel}
        primaryLabel={t("Next")}
        immediatePrimaryAction={onNext}
        trackingName={"tutoring_weeks_selected"}
        trackingSharedProperties={{
          activityUid: activity.uid,
          enrollmentDuration
        }}
        sx={
          !showCancel
            ? {}
            : {
                marginTop: "1em",
                paddingTop: "1em",
                borderTop: "1px solid",
                borderColor: "gray300",
                justifyContent: "space-between"
              }
        }
      />
    </form>
  );
}

EnrollmentMeetingPurchaseDuration.fragments = {
  activity: gql`
    fragment EnrollmentMeetingPurchaseDuration_activity on Activity {
      uid
      title
      titleTranslated
      weekly_meetings
      duration_weeks
      price_cents
      ...RefundPolicy_activity
    }
    ${RefundPolicy.fragments.activity}
  `
};

const OnetimeCard = ({
  startTime,
  endTime,
  onClick,
  t,
  selected = false
}: {
  startTime: Date;
  endTime: Date;
  selected: boolean;
  t: TFunction;
  onClick: () => void;
}) => {
  const { timeZone } = useTimeZone();
  return (
    <DurationCard onClick={onClick} image={onetimeImage} selected={selected}>
      <Typography emphasized>{t`Meet Once`}</Typography>
      <Typography>
        {t`Meet`}{" "}
        {fullDurationStringWithStartTime(startTime, endTime, timeZone)}
      </Typography>
    </DurationCard>
  );
};

const RecurringCard = ({
  startTime,
  endTime,
  enrollmentDuration,
  setEnrollmentDuration,
  selected = false,
  isRecurringSchedulingAvailable = false,
  canPayLater = false,
  t,
  onClick
}: {
  startTime: Date;
  endTime: Date;
  enrollmentDuration: number;
  setEnrollmentDuration: (val: number) => void;
  selected: boolean;
  canPayLater: boolean;
  isRecurringSchedulingAvailable: boolean;
  t: TFunction;
  onClick: () => void;
}) => {
  const { userTimeZone } = useSession();

  return (
    <DurationCard onClick={onClick} image={recurringImage} selected={selected}>
      <Box flex sx={{ flexDirection: "row", alignItems: "center" }}>
        <Typography
          emphasized
          sx={{ marginRight: ".5em" }}
        >{t`Meet Weekly`}</Typography>
        <PayAsYouGoTag showToolTip={true} canPayLater={canPayLater} />
      </Box>
      {t("Save the weekly spot! Meet every {{meetingDayAndTime}}", {
        meetingDayAndTime: weeklyDayOfWeekWithDuration(
          startTime,
          endTime,
          // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
          userTimeZone
        )
      })}
      <EnrollmentDurationSelect
        disabled={!selected}
        enrollmentDuration={enrollmentDuration}
        setEnrollmentDuration={setEnrollmentDuration}
        isRecurringSchedulingAvailable={isRecurringSchedulingAvailable}
      />
    </DurationCard>
  );
};

export const DurationCard = ({
  onClick,
  onMouseEnter,
  onMouseLeave,
  selected,
  children,
  selectable = true,
  image
}: {
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  selected: boolean;
  children: React.ReactNode;
  selectable?: boolean;
  image: string;
}) => {
  return (
    <Box
      flex
      sx={{
        position: "relative",
        gap: "0.5em",
        paddingY: "0.5em",
        paddingX: "1em",
        alignItems: "flex-start",
        flexWrap: "wrap",
        borderRadius: "16px",
        border: "2px solid",
        borderColor: selected ? "primary.600" : "grey.300",
        color: !selected ? "grey.600" : "",

        "&:hover": selectable && {
          backgroundColor: "grey.50",
          borderColor: "primary.600",
          "> *": {
            color: "black"
          }
        }
      }}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Box>
        <Image
          src={image}
          width={30}
          sx={{
            marginRight: "1em"
          }}
        />
      </Box>
      <Box
        flex
        sx={{
          flexDirection: "column",
          gap: "0.5em",
          alignItems: "flex-start",
          width: "80%"
        }}
      >
        {children}
      </Box>
      {selected && selectable && (
        <Box
          sx={{
            position: "absolute",
            bottom: -2,
            right: -2,
            backgroundColor: "primary.600",
            padding: "12px",
            borderBottomRightRadius: "1em",
            borderTopLeftRadius: "1em",
            lineHeight: 1
          }}
        >
          <Icon icon={faCheck} sx={{ color: "common.white" }} />
        </Box>
      )}
    </Box>
  );
};
